(function () {
  'use strict';

  const tidy = value => String(value || '').trim();

  function normalise(values) {
    const result = {};
    for (const name of ['first_name', 'last_name', 'email', 'company', 'delivery_country', 'preferred_contact', 'phone', 'message']) result[name] = tidy(values[name]);
    for (const name of ['marketing_consent']) result[name] = values[name] === true;
    result.preferred_contact ||= 'Email';
    result.quantity = tidy(values.quantity) === '' ? null : Number(tidy(values.quantity));
    for (const name of ['first_name', 'last_name', 'email', 'company', 'delivery_country']) {
      if (!result[name] || (name === 'delivery_country' && result[name] === '---')) throw new Error('Please complete the required fields.');
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(result.email)) throw new Error('Please enter a valid email address.');
    if (result.quantity !== null && (!/^\d+$/.test(tidy(values.quantity)) || !Number.isSafeInteger(result.quantity) || result.quantity < 1)) throw new Error('Enter a whole-number quantity of at least 1.');
    if (!['Email', 'Phone', 'WhatsApp'].includes(result.preferred_contact)) throw new Error('Please select a contact method.');
    if (result.preferred_contact !== 'Email' && !result.phone) throw new Error('Please enter a phone number for your chosen contact method.');
    if (result.preferred_contact === 'Email') result.phone = '';
    return result;
  }

  function buildEvent(values, context, id) {
    const input = normalise(values);
    const properties = {
      enquiry_id: id,
      first_name: input.first_name,
      last_name: input.last_name,
      product: context.product,
      product_id: context.product_id,
      product_handle: context.product_handle,
      product_url: context.product_url,
      image_url: context.image_url,
      variant_id: context.variant_id,
      sku: context.sku,
      dimensions: context.dimensions,
      dimensions_qualification: context.dimensions_qualification,
      industry_tags: context.industry_tags,
      approved_pricing: [],
      pricing_tier: null,
      reference_unit_price_ex_vat: null,
      company: input.company,
      quantity: input.quantity,
      quantity_status: input.quantity === null ? 'Not specified' : 'Specified',
      quantity_unit: context.quantity_unit,
      delivery_country: input.delivery_country,
      preferred_contact: input.preferred_contact,
      phone: input.phone,
      message: input.message,
      marketing_consent: input.marketing_consent
    };
    const profileProperties = {
      b2b_enquirer: true,
      b2b_company: input.company,
      b2b_delivery_country: input.delivery_country,
      b2b_preferred_contact: input.preferred_contact
    };
    if (input.phone) profileProperties.b2b_phone = input.phone;
    if (input.marketing_consent) profileProperties.b2b_marketing_requested = true;
    return { data: { type: 'event', attributes: {
      unique_id: id,
      properties,
      metric: { data: { type: 'metric', attributes: { name: 'B2B Enquiry Submitted' } } },
      profile: { data: { type: 'profile', attributes: { email: input.email, first_name: input.first_name, last_name: input.last_name, properties: profileProperties } } }
    } } };
  }

  function buildSubscription(email) {
    return { data: { type: 'subscription', attributes: {
      custom_source: 'B2B Product Enquiry',
      profile: { data: { type: 'profile', attributes: { email, subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } } } } }
    }, relationships: { list: { data: { type: 'list', id: 'WWN83b' } } } } };
  }

  async function post(path, body) {
    const response = await fetch('https://a.klaviyo.com/client/' + path + '?company_id=TJ3xFH', {
      method: 'POST', headers: { 'Content-Type': 'application/json', revision: '2026-04-15' }, body: JSON.stringify(body)
    });
    if (!response.ok) throw new Error('Request not accepted (' + response.status + ').');
  }

  function createSubmission(send = post, uuid = () => crypto.randomUUID(), notify = () => {}) {
    let entries = new Map();
    let active = null;
    let busy = false;
    async function subscribe(entry) {
      entry.subscription_status = 'pending';
      notify(entry);
      try {
        await send('subscriptions', buildSubscription(entry.email));
        entry.subscription_status = 'accepted';
      } catch (_) {
        entry.subscription_status = 'failed';
      }
      notify(entry);
      return entry;
    }
    return {
      async submit(values, context) {
        if (busy) return null;
        const input = normalise(values);
        const fingerprint = JSON.stringify(buildEvent(input, context, ''));
        if (!entries.has(fingerprint)) {
          const id = uuid();
          entries.set(fingerprint, { id, body: buildEvent(input, context, id), email: input.email, consent: input.marketing_consent, event_accepted: false, subscription_status: 'not_requested' });
        }
        active = entries.get(fingerprint);
        const entry = active;
        if (entry.event_accepted) return entry;
        busy = true;
        try {
          await send('events', entry.body);
          entry.event_accepted = true;
          notify(entry);
          if (entry.consent) await subscribe(entry);
          return entry;
        } finally { busy = false; }
      },
      async retrySubscription() {
        if (busy || !active?.event_accepted || !active.consent || active.subscription_status !== 'failed') return null;
        busy = true;
        try { return await subscribe(active); } finally { busy = false; }
      },
      reset() { if (!busy) { entries = new Map(); active = null; } },
      get busy() { return busy; }
    };
  }

  function readContext(modal) {
    const description = document.getElementById(modal.dataset.descriptionId);
    const dimensions = description?.querySelector('[data-b2b-dimensions]');
    const dimensionElements = [];
    let inDimensions = false;
    let proposed = false;
    if (!dimensions) {
      for (const element of description?.children || []) {
        const label = tidy(element.textContent);
        const heading = /^H[2-4]$/.test(element.tagName) || (element.tagName === 'P' && element.children.length === 1 && element.firstElementChild.tagName === 'STRONG');
        if (heading) {
          inDimensions = /^(Proposed dimensions|Dimensions|Voorgestelde afmetingen|Afmetingen|Vorgeschlagene Maße|Maße)$/i.test(label);
          if (inDimensions) proposed = /^(Proposed|Voorgestelde|Vorgeschlagene)/i.test(label);
        } else if (inDimensions) dimensionElements.push(element);
      }
    }
    return {
      product: modal.dataset.product,
      product_id: modal.dataset.productId,
      product_handle: modal.dataset.productHandle,
      product_url: modal.dataset.productUrl,
      image_url: modal.dataset.imageUrl,
      variant_id: modal.dataset.variantId,
      sku: modal.dataset.sku,
      industry_tags: JSON.parse(modal.dataset.industryTags).filter(tag => tag.startsWith('b2b-industry-')),
      dimensions: dimensions ? tidy(dimensions.textContent) : dimensionElements.map(element => tidy(element.textContent)).join('\n'),
      dimensions_qualification: (dimensions ? dimensions.dataset.b2bDimensions === 'proposed' : proposed) ? 'Proposed dimensions' : 'Dimensions',
      quantity_unit: (dimensions?.dataset.b2bQuantityUnit || modal.dataset.quantityUnit) === 'set' ? 'set' : 'item'
    };
  }

  function displayError(message, strings) {
    const key = ({
      'Please complete the required fields.': 'required_fields',
      'Please enter a valid email address.': 'valid_email',
      'Enter a whole-number quantity of at least 1.': 'valid_quantity',
      'Please select a contact method.': 'contact_method',
      'Please enter a phone number for your chosen contact method.': 'phone_required'
    })[message] || 'submission_failed';
    return strings[key];
  }

  if (typeof module !== 'undefined' && module.exports) module.exports = { normalise, buildEvent, buildSubscription, createSubmission, readContext, displayError };
  if (typeof window === 'undefined' || customElements.get('b2b-enquiry')) return;

  customElements.define('b2b-enquiry', class extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;
      queueMicrotask(() => this.initialise());
    }
    initialise() {
      const modal = this.closest('[data-b2b-modal]');
      const strings = JSON.parse(this.dataset.b2bStrings);
      const form = this.querySelector('form');
      const field = name => form.elements.namedItem(name);
      field('delivery_country').querySelector('option[value="---"]')?.remove();
      const wrap = this.querySelector('[data-b2b-form-wrap]');
      const success = this.querySelector('[data-b2b-success]');
      const error = this.querySelector('[data-b2b-error]');
      const submit = form.querySelector('[type="submit"]');
      const subscription = this.querySelector('[data-b2b-subscription-status]');
      const retry = this.querySelector('[data-b2b-subscription-retry]');
      const another = this.querySelector('[data-b2b-another]');
      const context = readContext(modal);
      const description = document.getElementById(modal.dataset.descriptionId);
      const read = () => {
        const values = Object.fromEntries(new FormData(form));
        for (const name of ['marketing_consent']) values[name] = field(name).checked;
        return values;
      };
      const controller = createSubmission(post, () => crypto.randomUUID(), entry => {
        modal.dataset.subscriptionStatus = entry.subscription_status;
        if (entry.event_accepted) {
          const wasHidden = success.hidden;
          wrap.hidden = true;
          success.hidden = false;
          this.querySelector('[data-b2b-reference]').textContent = entry.id;
          if (wasHidden) success.focus();
        }
        subscription.hidden = entry.subscription_status === 'not_requested';
        subscription.textContent = ({ pending: strings.subscription_pending, accepted: strings.subscription_accepted, failed: strings.subscription_failed })[entry.subscription_status] || '';
        retry.hidden = entry.subscription_status !== 'failed';
        another.disabled = entry.subscription_status === 'pending';
        modal.dispatchEvent(new CustomEvent('b2b:subscription-status', { bubbles: true, detail: { enquiry_id: entry.id, status: entry.subscription_status } }));
      });
      const update = () => {
        const needsPhone = field('preferred_contact').value !== 'Email';
        field('phone').required = needsPhone;
        field('phone').disabled = !needsPhone;
        this.querySelector('[data-b2b-phone-wrap]').hidden = !needsPhone;
      };
      form.addEventListener('input', update);
      form.addEventListener('change', update);
      form.addEventListener('submit', async event => {
        event.preventDefault();
        if (controller.busy) return;
        error.hidden = true;
        update();
        if (!form.reportValidity()) return;
        submit.disabled = true;
        form.setAttribute('aria-busy', 'true');
        try { await controller.submit(read(), context); }
        catch (failure) {
          error.textContent = displayError(failure.message, strings);
          error.hidden = false;
          error.focus();
        } finally { submit.disabled = false; form.removeAttribute('aria-busy'); }
      });
      retry.addEventListener('click', async () => {
        retry.disabled = true;
        try { await controller.retrySubscription(); } finally { retry.disabled = false; }
      });
      another.addEventListener('click', () => {
        if (controller.busy) return;
        controller.reset();
        form.reset();
        success.hidden = true;
        wrap.hidden = false;
        error.hidden = true;
        update();
        field('first_name').focus();
      });
      update();
      customElements.whenDefined('x-modal').then(() => {
        modal.addEventListener('dialog:before-show', () => {
          modal.style.opacity = '1';
          modal.getShadowPartByName('overlay').style.opacity = '1';
          modal.getShadowPartByName('content').style.transform = 'translateY(0)';
        });
        const links = [
          ...document.querySelectorAll('[data-b2b-enquiry-target="' + modal.id + '"]'),
          ...Array.from(description.querySelectorAll('a[href]')).filter(link =>
            new URL(link.href, document.baseURI).pathname.replace(/\/$/, '').endsWith('/pages/business-inquiries'))
        ];
        links.forEach(link => {
          link.setAttribute('aria-controls', modal.id);
          link.setAttribute('aria-haspopup', 'dialog');
        });
      });
    }
  });
})();
